import type { ScreenContext } from "../../shell/types";
import { autosaveStatus, createAutosave } from "../../ui/autosave";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { card, chip, ib, table } from "../../ui/record";
import { archiveModal, field, refuse, select } from "../clients/forms";
import { pageHead } from "../empty";
import { setupBar, setupQuery, type Picker } from "../../ui/setup-bar";
import type { ChecklistSettings } from "../../../server/core/checklists";

// Settings › Workflows (brief 1B-7, mockup settings/workflows): four tabs. Workflows: every flow
// with its stages; one opens to edit its stages (name, what's needed to leave, If missing, owner,
// timer), which changes the draft of its next version until Publish. Forms: Draft → Approved; an
// approved form is locked and a change makes the next version. Questions: the one bank, archived
// with a reason, never deleted. Blocks: who can release a block, and the fix list. Workflows,
// Forms and Questions each show the module you came from plus Shared, with a picker (brief 1E-7).
// An episode workflow (brief 1E-25, mockup v77 and v85) holds a checklist on each stage in place
// of its leave rule: the Stage checklist card with the stage picker, Add item, each item's pencil
// and Archive, and the stage's Moves on by itself switch.

type Tab = "flows" | "forms" | "questions" | "blocks";
const TABS: [Tab, string][] = [
  ["flows", "Workflows"],
  ["forms", "Forms"],
  ["questions", "Questions"],
  ["blocks", "Blocks"],
];
/** The tab last open, so coming back from a workflow lands where you were. */
let lastTab: Tab = "flows";

interface Flow {
  key: string;
  name: string;
  version: number | null;
  draft: boolean;
  stages: string[];
}
interface Stage {
  position: number;
  name: string;
  leaveRule: string;
  ifMissing: string;
  ownerRole: string | null;
  timerHours: number | null;
  movesOnOwn: boolean;
}
interface FlowView {
  key: string;
  name: string;
  version: number;
  status: "draft" | "published";
  published: number | null;
  inUse: { version: number; records: number }[];
  stages: Stage[];
  roles: { id: string; name: string }[];
  leaveRules: { value: string; label: string }[];
  ifMissing: { value: string; label: string }[];
  /** An episode workflow's checklists (brief 1E-25); null for a flow still on leave rules. */
  checklist: ChecklistSettings | null;
}
interface Form {
  id: string;
  name: string;
  usedAt: string;
  version: number;
  status: "draft" | "approved";
  questions: { key: string; label: string }[];
  versions: { version: number; status: string; questions: string[] }[];
}
interface Forms {
  picker?: Picker;
  forms: Form[];
  bank: { key: string; label: string; archived: boolean }[];
}
interface Question {
  id: string;
  key: string;
  label: string;
  answerLabel: string;
  savedOn: string;
  inForms: number;
  archived: boolean;
  archiveReason: string | null;
}
interface Blocks {
  roles: { id: string; name: string; admin: boolean; canRelease: boolean }[];
  fixList: {
    id: string;
    when: string;
    what: string;
    why: string;
    releasedBy: string;
    fixed: boolean;
    fixedBy: string | null;
  }[];
  canTick: boolean;
}
type Res<T> = { ok: boolean; status: number; body: T & { error?: string } };
/** The setup lists (brief 1E-7) ask with where you came from and their picker's value. */
const SETUP_LIST = /^\/(workflows|forms|questions)(\/[0-9a-f-]{36}(\/approve)?)?$/;
const call = <T>(method: string, path: string, body?: unknown) => {
  const m = SETUP_LIST.exec(path);
  const q = m ? setupQuery(m[1] as string) : "";
  return api<T>(method, `/api/settings${path}${q}`, body) as Promise<Res<T>>;
};

const failed = (what: string) =>
  el("p", { class: "note", role: "alert" }, `${what} couldn't be loaded.`);

export async function workflowsScreen(host: HTMLElement, ctx: ScreenContext) {
  if (ctx.record) return workflowPage(host, ctx, ctx.record);
  const page = el("div", { class: "record" });
  host.append(page);
  let tab: Tab = lastTab;

  const tabs = el("div", { class: "tabs", role: "tablist", "aria-label": "Workflows" });
  const body = el("div");

  function drawTabs() {
    tabs.replaceChildren(
      ...TABS.map(([k, label]) => {
        const b = el(
          "button",
          { type: "button", role: "tab", "aria-selected": String(tab === k) },
          label,
        );
        b.addEventListener("click", () => {
          tab = lastTab = k;
          void draw();
        });
        return b;
      }),
    );
  }

  async function draw() {
    drawTabs();
    if (tab === "flows") return flowsTab();
    if (tab === "forms") return formsTab();
    if (tab === "questions") return questionsTab();
    return blocksTab();
  }

  /** The head, the tabs, then the tab's own content. */
  function show(line: string, actions: Node[], ...content: Node[]) {
    body.replaceChildren(...content);
    // The Workflows tab is titled "Workflows & stages", as in the mockup.
    const head = pageHead(
      tab === "flows" ? { ...ctx, screen: { ...ctx.screen, title: "Workflows & stages" } } : ctx,
      line,
      actions,
      tab === "flows" ? null : "Company settings",
    );
    page.replaceChildren(head, tabs, body);
  }

  // ---- Workflows ----------------------------------------------------------------------------------
  async function flowsTab() {
    const res = await call<{ workflows: Flow[]; picker?: Picker }>("GET", "/workflows");
    if (tab !== "flows") return;
    const line = "Every flow is a list of stages.";
    if (!res.ok) return show(line, [], failed("The workflows"));
    const rows = res.body.workflows;
    let all = false;
    const holder = el("div");
    const drawList = () => {
      // The mockup's table shows ten, then "Show N more".
      const shown = all ? rows : rows.slice(0, 10);
      holder.replaceChildren(
        table<Flow>(
          [
            {
              h: "Workflow",
              f: (w) =>
                el(
                  "span",
                  {},
                  el("b", {}, w.name),
                  w.draft ? el("span", {}, " ", chip("Draft", "warn")) : null,
                ),
            },
            { h: "Stages", f: (w) => w.stages.join(" → ") },
          ],
          shown,
          (w) => ctx.go(`/settings/workflows/${w.key}`),
          "Workflows",
        ),
      );
      if (!all && rows.length > 10) {
        const more = el(
          "button",
          { type: "button", class: "btn sm ghost" },
          icon("chevron-down"),
          ` Show ${rows.length - 10} more`,
        );
        more.addEventListener("click", () => {
          all = true;
          drawList();
        });
        holder.append(el("div", { style: "margin-top:6px" }, more));
      }
    };
    drawList();
    show(
      line,
      [],
      setupBar("workflows", "Workflows", res.body.picker, () => void draw()),
      el(
        "p",
        { class: "small muted" },
        "Each client type uses the same workflow and changes only what's different (skip a stage, flag instead of block).",
      ),
      holder,
    );
  }

  // ---- Forms ---------------------------------------------------------------------------------------
  async function formsTab() {
    const res = await call<Forms>("GET", "/forms");
    if (tab !== "forms") return;
    const line = "Stages, forms and questions for each part of the work.";
    const add = el("button", { type: "button", class: "btn pri" }, icon("plus"), " New form");
    add.addEventListener("click", newForm);
    if (!res.ok) return show(line, [], failed("The forms"));
    const view = res.body;
    const approve = (f: Form) => async () => {
      const r = await call<Forms>("POST", `/forms/${f.id}/approve`);
      if (!r.ok) return toast("It wasn't approved", r.body.error);
      toast(
        `${f.name} approved`,
        `Version ${f.version} is locked. A change makes the next version.`,
      );
      void draw();
    };
    show(
      line,
      [add],
      setupBar("forms", "Forms", view.picker, () => void draw()),
      el(
        "section",
        { class: "card" },
        table<Form>(
          [
            { h: "Form", f: (f) => el("b", {}, f.name) },
            { h: "Used at", f: (f) => f.usedAt },
            { h: "Version", f: (f) => `v${f.version}` },
            {
              h: "Status",
              f: (f) =>
                chip(
                  f.status === "approved" ? "Approved" : "Draft",
                  f.status === "approved" ? "ok" : "warn",
                ),
            },
            {
              h: "",
              f: (f) => {
                const acts = el(
                  "div",
                  { class: "actions" },
                  ib("eye", `Preview ${f.name}`, () => preview(f)),
                  ib("pencil", `Change questions on ${f.name}`, () => editForm(f, view)),
                );
                if (f.status === "draft") {
                  const b = el(
                    "button",
                    { type: "button", class: "btn sm" },
                    icon("check"),
                    " Approve",
                  );
                  b.setAttribute("aria-label", `Approve ${f.name}`);
                  b.addEventListener("click", approve(f));
                  acts.append(b);
                }
                return acts;
              },
            },
          ],
          view.forms,
          undefined,
          "Forms",
        ),
        el(
          "p",
          { class: "small muted" },
          "Approved forms are locked; a change makes the next version.",
        ),
      ),
    );
  }

  function preview(f: Form) {
    openModal({
      title: f.name,
      sub: `${f.usedAt} · the questions on each version.`,
      body: f.versions.map((v) =>
        el(
          "section",
          { class: "card", style: "margin:8px 0" },
          el(
            "div",
            { class: "cardhead", style: "margin:0 0 4px" },
            el("h3", { class: "h3s", style: "margin:0" }, `Version ${v.version}`),
            chip(
              v.status === "approved" ? "Approved" : "Draft",
              v.status === "approved" ? "ok" : "warn",
            ),
          ),
          v.questions.length
            ? el("ol", { class: "small" }, ...v.questions.map((q) => el("li", {}, q)))
            : el("p", { class: "small muted" }, "No shared questions on this version."),
        ),
      ),
    });
  }

  function newForm() {
    const name = field("Name", "", { maxlength: "80" });
    const used = field("Used at", "", {
      maxlength: "40",
      placeholder: "Visit, Assessment, Intake…",
    });
    const go = el("button", { type: "button", class: "btn pri" }, icon("plus"), " Start draft");
    const m = openModal({
      title: "New form",
      sub: "It starts as a draft, version 1. Approve it when it's ready.",
      body: [el("div", { class: "form", style: "max-width:none" }, name.el, used.el)],
      actions: [go],
    });
    name.input.focus();
    go.addEventListener("click", async () => {
      go.disabled = true;
      const r = await call<Forms>("POST", "/forms", {
        name: name.input.value,
        usedAt: used.input.value,
      });
      go.disabled = false;
      if (!r.ok) return refuse(m.note, r.body.error);
      m.close();
      toast("New form started as a draft");
      void draw();
    });
  }

  function editForm(f: Form, view: Forms) {
    const had = new Set(f.questions.map((q) => q.key));
    const boxes = view.bank
      .filter((q) => !q.archived || had.has(q.key))
      .map((q) => {
        const input = el("input", { type: "checkbox", value: q.key, checked: had.has(q.key) });
        return {
          input,
          el: el(
            "label",
            { class: "ptask" },
            input,
            el("span", {}, q.label, " ", el("code", { class: "small muted" }, q.key)),
          ),
        };
      });
    const save = el("button", { type: "button", class: "btn pri" }, icon("check"), " Save");
    const m = openModal({
      title: `Questions on ${f.name}`,
      sub:
        f.status === "approved"
          ? `Version ${f.version} is approved and locked. Saving makes version ${f.version + 1} as a draft.`
          : `Version ${f.version} is a draft: saving changes it.`,
      body: [
        el(
          "div",
          { class: "form", style: "max-width:none;display:grid;gap:6px" },
          ...boxes.map((b) => b.el),
        ),
      ],
      actions: [save],
      wide: true,
    });
    save.addEventListener("click", async () => {
      save.disabled = true;
      const keys = boxes.filter((b) => b.input.checked).map((b) => b.input.value);
      const r = await call<Forms>("PUT", `/forms/${f.id}`, { questionKeys: keys });
      save.disabled = false;
      if (!r.ok) return refuse(m.note, r.body.error);
      m.close();
      const now = r.body.forms.find((x) => x.id === f.id);
      toast(
        "Form saved",
        now
          ? `Version ${now.version} · ${now.status === "draft" ? "Draft" : "Approved"}`
          : undefined,
      );
      void draw();
    });
  }

  // ---- Questions ---------------------------------------------------------------------------------
  async function questionsTab() {
    const res = await call<{ questions: Question[]; picker?: Picker }>("GET", "/questions");
    if (tab !== "questions") return;
    const line = "Stages, forms and questions for each part of the work.";
    const add = el("button", { type: "button", class: "btn pri" }, icon("plus"), " New question");
    add.addEventListener("click", newQuestion);
    if (!res.ok) return show(line, [], failed("The questions"));
    show(
      line,
      [add],
      setupBar("questions", "Questions", res.body.picker, () => void draw()),
      el(
        "section",
        { class: "card" },
        table<Question>(
          [
            { h: "Key", f: (q) => el("code", {}, q.key) },
            {
              h: "Question",
              f: (q) =>
                el(
                  "span",
                  {},
                  el("b", {}, q.label),
                  q.archived && q.archiveReason
                    ? el("div", { class: "small muted" }, q.archiveReason)
                    : null,
                ),
            },
            { h: "Answer saved on", f: (q) => q.savedOn },
            { h: "In forms", num: true, f: (q) => String(q.inForms) },
            {
              h: "Status",
              f: (q) => (q.archived ? chip("Archived", "plain") : chip("Active", "ok")),
            },
            {
              h: "",
              num: true,
              f: (q) =>
                q.archived
                  ? ""
                  : ib("archive", `Archive ${q.key}`, () =>
                      archiveModal(
                        `question ${q.key}`,
                        `/api/settings/questions/${q.id}/archive`,
                        () => {
                          toast(
                            `${q.key} archived`,
                            "Archived questions can't be added to a form.",
                          );
                          return draw();
                        },
                      ),
                    ),
            },
          ],
          res.body.questions,
          undefined,
          "Shared questions",
        ),
        el("p", { class: "small muted" }, "One set of questions shared by every form."),
      ),
    );
  }

  function newQuestion() {
    const key = field("Key", "", { maxlength: "60", placeholder: "vitals.pulse" });
    const label = field("Question", "", { maxlength: "200" });
    const type = select(
      "Answer type",
      [
        ["text", "Text"],
        ["number", "Number"],
        ["yes_no", "Yes / No"],
        ["choice", "Pick one"],
        ["date", "Date"],
      ],
      "text",
    );
    const on = select(
      "Answer saved on",
      [
        ["patient", "Patient"],
        ["episode", "Episode"],
        ["visit", "Visit"],
      ],
      "visit",
    );
    const go = el("button", { type: "button", class: "btn pri" }, icon("plus"), " Add question");
    const m = openModal({
      title: "New question",
      sub: "A short name for the question. It can't be changed later.",
      body: [
        el("div", { class: "form", style: "max-width:none" }, key.el, label.el, type.el, on.el),
      ],
      actions: [go],
    });
    key.input.focus();
    go.addEventListener("click", async () => {
      go.disabled = true;
      const r = await call("POST", "/questions", {
        key: key.input.value,
        label: label.input.value,
        answerType: type.input.value,
        savedOn: on.input.value,
      });
      go.disabled = false;
      if (!r.ok) return refuse(m.note, r.body.error);
      m.close();
      toast("Question added");
      void draw();
    });
  }

  // ---- Blocks --------------------------------------------------------------------------------------
  async function blocksTab() {
    const res = await call<Blocks>("GET", "/blocks");
    if (tab !== "blocks") return;
    const line = "Stages, forms and questions for each part of the work.";
    if (!res.ok) return show(line, [], failed("Blocks"));
    const v = res.body;
    const toFix = v.fixList.filter((f) => !f.fixed).length;
    show(
      line,
      [],
      el(
        "div",
        { class: "grid2" },
        el(
          "section",
          { class: "card" },
          el("h2", {}, icon("unlock"), " Who can release a block"),
          el(
            "p",
            { class: "small muted" },
            "These roles can release a held bill or pay, with a reason.",
          ),
          el(
            "div",
            { style: "display:grid;gap:6px" },
            ...v.roles.map((r) => {
              const input = el("input", {
                type: "checkbox",
                checked: r.canRelease,
                disabled: r.admin || !v.canTick,
              });
              input.addEventListener("change", async () => {
                input.disabled = true;
                const x = await call<Blocks>("PUT", `/blocks/roles/${r.id}`, {
                  canRelease: input.checked,
                });
                input.disabled = false;
                if (!x.ok) {
                  input.checked = !input.checked;
                  return toast("That wasn't saved", x.body.error);
                }
                toast(
                  input.checked
                    ? `${r.name} can release a block`
                    : `${r.name} can't release a block`,
                );
              });
              return el(
                "label",
                { class: "ptask" },
                input,
                r.name,
                r.admin ? el("span", { class: "small muted" }, " (always)") : null,
              );
            }),
          ),
        ),
        el(
          "section",
          { class: "card" },
          el("h2", {}, icon("checklist"), ` Fix list · ${toFix}`),
          el(
            "p",
            { class: "small muted" },
            "Every release lands here so the rule behind it can be fixed.",
          ),
          v.fixList.length
            ? table<Blocks["fixList"][number]>(
                [
                  { h: "When", f: (f) => f.when },
                  {
                    h: "What was held",
                    f: (f) =>
                      el(
                        "span",
                        {},
                        el("b", {}, f.what),
                        el("div", { class: "small muted" }, f.why),
                      ),
                  },
                  { h: "Released by", f: (f) => f.releasedBy },
                  {
                    h: "",
                    f: (f) => {
                      if (f.fixed) return chip("Fixed", "ok");
                      const b = el(
                        "button",
                        { type: "button", class: "btn sm" },
                        icon("check"),
                        " Fixed",
                      );
                      b.setAttribute("aria-label", `Mark fixed: ${f.what}`);
                      b.addEventListener("click", async () => {
                        b.disabled = true;
                        const x = await call<Blocks>("POST", `/blocks/fix/${f.id}/fixed`);
                        if (!x.ok) {
                          b.disabled = false;
                          return toast("That wasn't saved", x.body.error);
                        }
                        toast("Marked fixed");
                        void draw();
                      });
                      return b;
                    },
                  },
                ],
                v.fixList,
                undefined,
                "Fix list",
              )
            : el("p", { class: "muted small" }, "Nothing released yet."),
        ),
      ),
    );
  }

  await draw();
}

// ---- One workflow, opened ------------------------------------------------------------------------

async function workflowPage(host: HTMLElement, ctx: ScreenContext, key: string) {
  const page = el("div", { class: "record" });
  host.append(page);
  const first = await call<FlowView>("GET", `/workflows/${encodeURIComponent(key)}`);
  if (!first.ok) {
    page.append(
      el(
        "div",
        { class: "blank", role: "alert" },
        icon("circle-slash", "big"),
        el("h2", {}, "Not available"),
        el("p", { class: "muted" }, "This workflow isn't here."),
      ),
    );
    return;
  }
  let view = first.body;
  ctx.crumb(view.name);
  const status = autosaveStatus();

  const versionLine = () => {
    const keep = view.inUse.map((u) => `${u.records} on version ${u.version}`).join(", ");
    return view.status === "draft"
      ? `Version ${view.version} · draft, not published yet. Records keep the version they started on${keep ? ` (${keep})` : ""}.`
      : `Version ${view.version} · published. A change starts version ${view.version + 1} as a draft${keep ? `; records keep theirs (${keep})` : ""}.`;
  };
  const line = el("p", { class: "small muted", role: "status" }, versionLine());

  const publish = el("button", { type: "button", class: "btn pri" }, icon("check"), " Publish");
  publish.addEventListener("click", async () => {
    publish.disabled = true;
    const r = await call<FlowView>("POST", `/workflows/${encodeURIComponent(key)}/publish`);
    publish.disabled = false;
    if (!r.ok) return toast("Nothing was published", r.body.error);
    view = r.body;
    toast("Workflow published", `Version ${view.version}. Records already in it keep theirs.`);
    draw();
  });
  // A secondary head button is an icon with its tip, as in the mockup.
  const addStage = el(
    "button",
    { type: "button", class: "btn ib", "aria-label": "Add stage", "data-tip": "Add stage" },
    icon("plus"),
  );
  addStage.addEventListener("click", async () => {
    const r = await call<FlowView>("POST", `/workflows/${encodeURIComponent(key)}/stages`);
    if (!r.ok) return toast("No stage was added", r.body.error);
    view = r.body;
    toast("Stage added", "Name it, then Publish.");
    draw();
  });

  /** One stage's row: every field saves a moment after the last change, into the draft. */
  function stageRow(s: Stage) {
    const name = el("input", {
      type: "text",
      value: s.name,
      maxlength: "60",
      style: "min-width:11em",
      "aria-label": `Stage ${s.position + 1} name`,
    });
    const leave = el(
      "select",
      { "aria-label": `Stage ${s.position + 1}: to leave this stage` },
      ...view.leaveRules.map((o) =>
        el("option", { value: o.value, selected: o.value === s.leaveRule }, o.label),
      ),
    );
    const missing = el(
      "select",
      { "aria-label": `Stage ${s.position + 1}: if missing` },
      ...view.ifMissing.map((o) =>
        el("option", { value: o.value, selected: o.value === s.ifMissing }, o.label),
      ),
    );
    const owner = el(
      "select",
      { "aria-label": `Stage ${s.position + 1}: owner` },
      el("option", { value: "", selected: !s.ownerRole }, "Nobody set"),
      ...view.roles.map((r) =>
        el("option", { value: r.id, selected: r.id === s.ownerRole }, r.name),
      ),
    );
    const timer = el("input", {
      type: "number",
      min: "1",
      max: "8760",
      step: "1",
      value: s.timerHours === null ? "" : String(s.timerHours),
      placeholder: "None",
      style: "width:6em",
      "aria-label": `Stage ${s.position + 1}: timer in hours`,
    });
    const own = el("input", {
      type: "checkbox",
      checked: s.movesOnOwn,
      "aria-label": `Stage ${s.position + 1}: moves on by itself when done`,
    });
    const saver = createAutosave({
      save: async () => {
        const r = await call<FlowView>(
          "PUT",
          `/workflows/${encodeURIComponent(key)}/stages/${s.position}`,
          {
            name: name.value,
            leaveRule: leave.value,
            ifMissing: missing.value,
            ownerRole: owner.value || null,
            timerHours: timer.value === "" ? null : Number(timer.value),
            ...(view.checklist ? { movesOnOwn: own.checked } : {}),
          },
        );
        if (!r.ok) return { ok: false, error: r.body.error };
        view = r.body;
        line.textContent = versionLine();
        return { ok: true };
      },
      onStatus: status.show,
    });
    for (const i of [name, timer]) i.addEventListener("input", () => saver.changed());
    for (const i of [leave, missing, owner, own])
      i.addEventListener("change", () => saver.changed());
    return { s, name, leave, missing, owner, timer, own };
  }

  function draw() {
    line.textContent = versionLine();
    const rows = view.stages.map(stageRow);
    page.replaceChildren(
      el(
        "div",
        { class: "head" },
        el(
          "div",
          { class: "t" },
          el("div", { class: "eyebrow" }, "Workflow"),
          el("h1", {}, icon("workflow", "h1i"), view.name),
          el("div", { class: "muted" }, `${view.stages.length} stages`),
        ),
        el("div", { class: "actions" }, publish, addStage),
      ),
      el(
        "ol",
        { class: "stepper" },
        ...view.stages.map((s) =>
          el(
            "li",
            {},
            el("span", { class: "bar" }),
            el(
              "span",
              { class: "lab" },
              el("span", { class: "n" }, String(s.position + 1)),
              s.name,
            ),
          ),
        ),
      ),
      card(
        "Stages",
        null,
        [status.el],
        line,
        table<(typeof rows)[number]>(
          view.checklist
            ? [
                { h: "#", f: (r) => String(r.s.position + 1) },
                { h: "Stage", f: (r) => r.name },
                {
                  h: "Moves on by itself",
                  f: (r) => el("label", { class: "ptask" }, r.own, " When done"),
                },
                { h: "Owner", f: (r) => r.owner },
                { h: "Timer (hours)", f: (r) => r.timer },
              ]
            : [
                { h: "#", f: (r) => String(r.s.position + 1) },
                { h: "Stage", f: (r) => r.name },
                { h: "To leave this stage", f: (r) => r.leave },
                { h: "If missing", f: (r) => r.missing },
                { h: "Owner", f: (r) => r.owner },
                { h: "Timer (hours)", f: (r) => r.timer },
              ],
          rows,
          undefined,
          "Stages",
        ),
        el(
          "p",
          { class: "small muted" },
          "Optional: move on. Flag: move on and tell the owner. Block: move on, flag, and hold the bill or the pay until it's fixed or released.",
        ),
      ),
      view.checklist ? checklistCard(view.checklist) : el("span"),
    );
  }

  let stageShown = 0;
  /** The Stage checklist card (mockup v77): what has to be true to leave the stage. */
  function checklistCard(c: ChecklistSettings): HTMLElement {
    const at = Math.min(stageShown, Math.max(view.stages.length - 1, 0));
    const pick = el(
      "select",
      { id: "wfst", "aria-label": "Stage" },
      ...view.stages.map((s) =>
        el("option", { value: String(s.position), selected: s.position === at }, s.name),
      ),
    );
    pick.addEventListener("change", () => {
      stageShown = Number(pick.value);
      draw();
    });
    const add = el("button", { type: "button", class: "btn sm" }, icon("plus"), " Add item");
    add.addEventListener("click", () => itemModal(c, at, null));
    const items = c.items.filter((i) => i.stage === at);
    const missLabel = (v: string) => c.ifMissing.find((x) => x.value === v)?.label ?? v;
    return el(
      "section",
      { class: "card", "aria-label": "Stage checklist" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("h2", {}, icon("checklist"), " Stage checklist"),
          el("span", { class: "small muted" }, "What has to be true to leave the stage"),
        ),
        el(
          "span",
          { class: "actions" },
          el("label", { class: "small", for: "wfst" }, "Stage"),
          pick,
          add,
        ),
      ),
      table<(typeof items)[number]>(
        [
          { h: "Item", f: (r) => el("b", {}, r.name) },
          {
            h: "Checked by",
            f: (r) =>
              chip(
                r.how === "app" ? `App · ${r.checks}` : r.checks,
                r.how === "app" ? "ok" : "info",
              ),
          },
          { h: "Client types", f: (r) => r.clientTypes },
          { h: "Owner", f: (r) => r.owner ?? "–" },
          { h: "Due", f: (r) => r.within || "–" },
          {
            h: "If missing",
            f: (r) =>
              chip(
                missLabel(r.ifMissing),
                r.ifMissing === "block" ? "bad" : r.ifMissing === "flag" ? "warn" : "plain",
              ),
          },
          {
            h: "",
            f: (r) =>
              el(
                "span",
                { class: "rowbtns" },
                ib("pencil", `Change ${r.name}`, () => itemModal(c, at, r)),
                ib("archive", `Archive ${r.name}`, () =>
                  archiveModal(
                    r.name,
                    `/api/settings/workflows/${encodeURIComponent(key)}/items/${r.id}/archive`,
                    async () => {
                      const v = await call<FlowView>(
                        "GET",
                        `/workflows/${encodeURIComponent(key)}`,
                      );
                      if (v.ok) view = v.body;
                      draw();
                    },
                  ),
                ),
              ),
          },
        ],
        items,
        undefined,
        "Stage checklist",
      ),
      items.length ? el("span") : el("p", { class: "small muted" }, "Nothing to check here yet."),
      el(
        "p",
        { class: "small muted", style: "margin:8px 0 0" },
        "Block holds billing only. Care is flagged and goes on.",
      ),
    );
  }

  /** Add or change an item (mockup v77's Add item sheet), saved in the draft. */
  function itemModal(
    c: ChecklistSettings,
    stage: number,
    row: ChecklistSettings["items"][number] | null,
  ) {
    const name = field("Item", row?.name ?? "", {
      maxlength: "80",
      placeholder: "Called the physician's office to confirm the order",
    });
    const radio = (v: "app" | "person", words: string) => {
      const i = el("input", {
        type: "radio",
        name: "wah",
        value: v,
        checked: (row?.how ?? "app") === v,
      });
      return { i, el: el("label", {}, i, ` ${words}`) };
    };
    const app = radio("app", "The app checks");
    const person = radio("person", "A person confirms");
    const how = el(
      "div",
      { class: "field" },
      el("label", {}, "How it's checked"),
      el("div", { class: "seg" }, app.el, person.el),
    );
    const fact = select(
      "What the app checks",
      c.facts.map((f) => [f.key, f.label] as const),
      row?.fact ?? c.facts[0]?.key ?? "",
    );
    const proof = select(
      "Proof from the person",
      c.proofs.map((p) => [p.value, p.label] as const),
      row?.proof ?? "none",
    );
    const types = c.clientTypes.map((t) => {
      const b = el("input", {
        type: "checkbox",
        value: t.id,
        checked: !!row?.clientTypeIds.includes(t.id),
      });
      return { b, el: el("label", { class: "tsr" }, b, ` ${t.name}`) };
    });
    const applies = el(
      "fieldset",
      { class: "tsf" },
      el("legend", {}, "Applies to"),
      el("span", { class: "small muted" }, "None picked: every client type"),
      el("div", { class: "tsgrid" }, ...types.map((t) => t.el)),
    );
    const owner = select(
      "Owner",
      [["", "Nobody set"] as const, ...view.roles.map((r) => [r.id, r.name] as const)],
      row?.ownerRole ?? "",
    );
    const within = field("Within (hours)", row?.withinHours ? String(row.withinHours) : "", {
      type: "number",
      min: "1",
      placeholder: "None",
    });
    const missing = select(
      "If missing",
      c.ifMissing.map((x) => [x.value, x.label] as const),
      row?.ifMissing ?? "flag",
    );
    const show = () => {
      fact.el.hidden = !app.i.checked;
      proof.el.hidden = app.i.checked;
    };
    app.i.addEventListener("change", show);
    person.i.addEventListener("change", show);
    show();
    const save = el(
      "button",
      { type: "button", class: "btn pri" },
      icon("check"),
      row ? " Save" : " Add",
    );
    save.addEventListener("click", async () => {
      save.disabled = true;
      const r = await call<FlowView>(
        row ? "PUT" : "POST",
        row
          ? `/workflows/${encodeURIComponent(key)}/items/${row.id}`
          : `/workflows/${encodeURIComponent(key)}/stages/${stage}/items`,
        {
          name: name.input.value,
          how: app.i.checked ? "app" : "person",
          fact: fact.input.value,
          proof: proof.input.value,
          clientTypeIds: types.filter((t) => t.b.checked).map((t) => t.b.value),
          ownerRole: owner.input.value || null,
          withinHours: within.input.value || null,
          ifMissing: missing.input.value,
        },
      );
      save.disabled = false;
      if (!r.ok) return refuse(m.note, r.body.error);
      view = r.body;
      m.close();
      toast(row ? "Item saved" : "Item added", "Saved in the draft · publish to use it");
      draw();
    });
    const m = openModal({
      title: row ? `Change ${row.name}` : `Add to ${view.stages[stage]?.name ?? ""}`,
      sub: view.name,
      body: [
        el(
          "div",
          { class: "form", style: "max-width:none" },
          name.el,
          how,
          fact.el,
          proof.el,
          applies,
          el("div", { class: "grid2", style: "gap:10px" }, owner.el, within.el, missing.el),
        ),
      ],
      actions: [save],
    });
    name.input.focus();
  }
  draw();
}
